import { api } from "@/api/axios.ts";
import { ApiIdObject, ApiOperationResult } from "@/types/common.types.ts";
import { ApiPagedData } from "@skailer-com/uikit/core";
import { ApiFormEdit } from "@/types/forms/form-edit.types.ts";
import {
  ApiFormTemplate,
  ApiFormTemplateDuplicatePayload,
  ApiFormTemplateFilter,
  ApiFormTemplateMovePayload,
  ApiFormTemplateSavePayload,
} from "@/types/forms/form-template.types.ts";
import { ApiForm, ApiFormView } from "@/types/forms/form-view.types.ts";
import {
  ApiPerformanceReview,
  ApiPerformanceReviewEdit,
  ApiPerformanceReviewExportPayload,
  ApiPerformanceReviewParticipantChart,
  ApiPerformanceReviewParticipantChartPayload,
  ApiPerformanceReviewParticipantChartSearch,
  ApiPerformanceReviewParticipantFeedback,
  ApiPerformanceReviewParticipantReport,
  ApiPerformanceReviewParticipantReportFilter,
  ApiPerformanceReviewParticipantReviewers,
  ApiPerformanceReviewParticipantReviewersSave,
  ApiPerformanceReviewParticipantSearchFilter,
  ApiPerformanceReviewParticipantShareSettingUpdate,
  ApiPerformanceReviewParticipantsSummary,
  ApiPerformanceReviewParticipantsSummaryFilter,
  ApiPerformanceReviewParticipantSummary,
  ApiPerformanceReviewResultSearchItem,
  ApiPerformanceReviewResultsFilter,
  ApiPerformanceReviewReviewerPreviewCount,
  ApiPerformanceReviewReviewerStatsFull,
  ApiPerformanceReviewReviewerTypeAggregatedResults,
  ApiPerformanceReviewReviewerTypeUpdateForm,
  ApiPerformanceReviewSaveReviewerTypes,
  ApiPerformanceReviewSearchItem,
  ApiPerformanceReviewsFilter,
  ApiPerformanceReviewUpdateDetails,
  ApiPerformanceReviewUpdateInvitation,
  ApiPerformanceReviewUpdateRatings,
  ApiPerformanceReviewUpdateReviewerTypeSettings,
  ApiPRReviewReopen,
} from "@/types/performance-review.types.ts";
import { ReminderSettingsPayload } from "@/types/reminder.types.ts";

export const apiPerformanceReviews = {
  getById: async function (id: number) {
    return api.get<ApiPerformanceReview>(`/api/v1/performance-reviews/${id}`);
  },
  search: async function (filter: ApiPerformanceReviewsFilter, signal: AbortSignal) {
    return api.post<ApiPagedData<ApiPerformanceReviewSearchItem>>(
      "/api/v1/performance-reviews/search",
      filter,
      signal
    );
  },
  getStats: async function (id: number) {
    return api.get<ApiPerformanceReviewReviewerStatsFull>(
      `/api/v1/performance-reviews/${id}/stats`
    );
  },
  launch: async function (id: number) {
    return api.post<ApiOperationResult>(`/api/v1/performance-reviews/${id}/launch`, {});
  },
  pause: async function (id: number) {
    return api.post<ApiOperationResult>(`/api/v1/performance-reviews/${id}/pause`, {});
  },
  complete: async function (id: number) {
    return api.post<ApiOperationResult>(`/api/v1/performance-reviews/${id}/complete`, {});
  },
  reopen: async function (id: number, payload: ApiPRReviewReopen) {
    return api.post<ApiOperationResult>(`/api/v1/performance-reviews/${id}/reopen`, payload);
  },
  delete: async function (id: number) {
    return api.delete<ApiOperationResult>(`/api/v1/performance-reviews/${id}`);
  },
  getByIdEdit: async function (id: number) {
    return api.get<ApiPerformanceReviewEdit>(`/api/v1/performance-reviews/setup/${id}`);
  },
  create: async function () {
    return api.post<ApiIdObject>("/api/v1/performance-reviews/setup", {});
  },
  updateDetails: async function (id: number, payload: ApiPerformanceReviewUpdateDetails) {
    return api.put<ApiOperationResult>(`/api/v1/performance-reviews/setup/${id}/details`, payload);
  },
  saveReviewerTypes: async function (id: number, payload: ApiPerformanceReviewSaveReviewerTypes) {
    return api.put<ApiOperationResult>(
      `/api/v1/performance-reviews/setup/${id}/reviewer-types`,
      payload
    );
  },
  getReviewerPreviewCount: async function (id: number) {
    return api.get<ApiPerformanceReviewReviewerPreviewCount[]>(
      `/api/v1/performance-reviews/setup/${id}/reviewers/preview-count`
    );
  },

  updateReviewerTypeForm: async function (
    id: number,
    reviewer_type_id: number,
    payload: ApiPerformanceReviewReviewerTypeUpdateForm
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/performance-reviews/setup/${id}/reviewer-types/${reviewer_type_id}/form`,
      payload
    );
  },
  updateReviewerTypeSettings: async function (
    id: number,
    payload: ApiPerformanceReviewUpdateReviewerTypeSettings
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/performance-reviews/setup/${id}/reviewer-types/settings`,
      payload
    );
  },
  updateInvitation: async function (id: number, payload: ApiPerformanceReviewUpdateInvitation) {
    return api.put<ApiOperationResult>(
      `/api/v1/performance-reviews/setup/${id}/invitation`,
      payload
    );
  },
  updateRatings: async function (id: number, payload: ApiPerformanceReviewUpdateRatings) {
    return api.put<ApiOperationResult>(`/api/v1/performance-reviews/setup/${id}/ratings`, payload);
  },
  updateReminders: async function (id: number, payload: ReminderSettingsPayload) {
    return api.put<ApiOperationResult>(
      `/api/v1/performance-reviews/setup/${id}/reminders`,
      payload
    );
  },
  reviewerTypeFormGetAll: async function (id: number) {
    return api.get<ApiForm[]>(`/api/v1/performance-reviews/${id}/reviewer-types/forms`);
  },
  reviewerTypeFormGetById: async function (reviewer_type_id: number) {
    return api.get<ApiForm>(`/api/v1/performance-reviews/reviewer-types/${reviewer_type_id}/form`);
  },
  reviewerTypeFormUpdate: async function (reviewer_type_id: number, formPayload: ApiFormEdit) {
    return api.put<ApiOperationResult>(
      `/api/v1/performance-reviews/reviewer-types/${reviewer_type_id}/form`,
      formPayload
    );
  },
  reviewerTypeFormReset: async function (reviewer_type_id: number) {
    return api.post<ApiOperationResult>(
      `/api/v1/performance-reviews/reviewer-types/${reviewer_type_id}/form/reset`,
      {}
    );
  },
  createPerformanceReviewTemplateFromReviewerType: async function (
    id: number,
    payload: ApiFormTemplateSavePayload
  ) {
    return api.post<ApiFormTemplate>(
      `/api/v1/performance-reviews/reviewer-types/${id}/templates`,
      payload
    );
  },
  getPerformanceReviewTemplateById: async function (id: number) {
    return api.get<ApiFormTemplate>(`/api/v1/performance-reviews/templates/${id}`);
  },
  searchPerformanceReviewTemplates: async function (
    filter: ApiFormTemplateFilter,
    signal: AbortSignal
  ) {
    return api.post<ApiPagedData<ApiFormTemplate>>(
      "/api/v1/performance-reviews/templates/search",
      filter,
      signal
    );
  },
  createPerformanceReviewTemplate: async function (payload: ApiFormTemplateSavePayload) {
    return api.post<ApiFormTemplate>(`/api/v1/performance-reviews/templates`, payload);
  },
  duplicatePerformanceReviewTemplate: async function (
    id: number,
    payload: ApiFormTemplateDuplicatePayload = {}
  ) {
    return api.post<ApiFormTemplate>(
      `/api/v1/performance-reviews/templates/${id}/duplicate`,
      payload
    );
  },
  updatePerformanceReviewTemplate: async function (
    id: number,
    payload: ApiFormTemplateSavePayload
  ) {
    return api.put<ApiFormTemplate>(`/api/v1/performance-reviews/templates/${id}`, payload);
  },
  performanceReviewTemplateFormGetById: async function (id: number) {
    return api.get<ApiForm>(`/api/v1/performance-reviews/templates/${id}/form`);
  },
  performanceReviewTemplateFormUpdate: async function (id: number, formPayload: ApiFormEdit) {
    return api.put<ApiOperationResult>(
      `/api/v1/performance-reviews/templates/${id}/form`,
      formPayload
    );
  },
  movePerformanceReviewTemplate: async function (id: number, payload: ApiFormTemplateMovePayload) {
    return api.put<ApiOperationResult>(`/api/v1/performance-reviews/templates/${id}/move`, payload);
  },
  deletePerformanceReviewTemplate: async function (id: number) {
    return api.delete<ApiOperationResult>(`/api/v1/performance-reviews/templates/${id}`);
  },
  participantsSummarySearch: async function (
    id: number,
    filter: ApiPerformanceReviewParticipantsSummaryFilter,
    signal: AbortSignal
  ) {
    return api.post<ApiPerformanceReviewParticipantsSummary>(
      `/api/v1/performance-reviews/${id}/participants/summary/search`,
      filter,
      signal
    );
  },
  participantReportByEmployee: async function (
    id: number,
    employee_id: number,
    filter: ApiPerformanceReviewParticipantReportFilter,
    signal: AbortSignal
  ) {
    return api.post<ApiPerformanceReviewParticipantReport>(
      `/api/v1/performance-reviews/${id}/participants/${employee_id}/report`,
      filter,
      signal
    );
  },
  participantShareSettingUpdateByEmployee: async function (
    id: number,
    employee_id: number,
    payload: ApiPerformanceReviewParticipantShareSettingUpdate
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/performance-reviews/${id}/participants/${employee_id}/share-settings`,
      payload
    );
  },
  participantSummaryByEmployee: async function (id: number, employee_id: number) {
    return api.get<ApiPerformanceReviewParticipantSummary>(
      `/api/v1/performance-reviews/${id}/participants/${employee_id}/summary`
    );
  },
  reviewerTypeGetAggregatedResults: async function (id: number, reviewer_type_id: number) {
    return api.get<ApiPerformanceReviewReviewerTypeAggregatedResults>(
      `/api/v1/performance-reviews/${id}/reviewer-types/${reviewer_type_id}/results`
    );
  },
  reviewerResults: async function (reviewerTypeId: number, respondentUuid: string) {
    return api.get<ApiFormView>(
      `/api/v1/performance-reviews/reviewer-types/${reviewerTypeId}/results/${respondentUuid}`
    );
  },
  questionResultsSearch: async function (
    filter: ApiPerformanceReviewResultsFilter,
    signal: AbortSignal
  ) {
    return api.post<ApiPagedData<ApiPerformanceReviewResultSearchItem>>(
      `/api/v1/performance-reviews/${filter.performance_review_id}/results/search`,
      filter,
      signal
    );
  },
  getParticipantReviewers: async function (
    id: number,
    employee_id: number,
    reviewer_type_id: number
  ) {
    return api.get<ApiPerformanceReviewParticipantReviewers>(
      `/api/v1/performance-reviews/${id}/participants/${employee_id}/reviewer-types/${reviewer_type_id}/reviewers`
    );
  },
  saveParticipantReviewers: async function (
    id: number,
    employee_id: number,
    reviewer_type_id: number,
    payload: ApiPerformanceReviewParticipantReviewersSave
  ) {
    return api.put<ApiOperationResult>(
      `/api/v1/performance-reviews/${id}/participants/${employee_id}/reviewer-types/${reviewer_type_id}/reviewers`,
      payload
    );
  },
  getParticipantChart: async function (
    performance_review_id: number,
    payload: ApiPerformanceReviewParticipantChartPayload,
    signal?: AbortSignal
  ) {
    return api.post<ApiPerformanceReviewParticipantChart>(
      `/api/v1/performance-reviews/${performance_review_id}/participants/chart`,
      payload,
      signal
    );
  },
  searchParticipantChart: async function (
    performance_review_id: number,
    payload: ApiPerformanceReviewParticipantChartPayload,
    signal?: AbortSignal
  ) {
    return api.post<ApiPerformanceReviewParticipantChartSearch>(
      `/api/v1/performance-reviews/${performance_review_id}/participants/chart/search`,
      payload,
      signal
    );
  },
  exportResults: async function (id: number, payload: ApiPerformanceReviewExportPayload) {
    return api.post<BlobPart>(`/api/v1/performance-reviews/${id}/export`, payload, undefined, {
      responseType: "blob",
    });
  },
  participantSearch: async function (filter: ApiPerformanceReviewParticipantSearchFilter) {
    return api.post<ApiPagedData<ApiPerformanceReviewParticipantFeedback>>(
      `/api/v1/performance-reviews/participants/search`,
      filter
    );
  },
  addLeaveFeedback: async function (reviewId: number, employeeId: number, reviewTypeId: number) {
    return api.post<{ reviewer_id: number }>(
      `/api/v1/performance-reviews/${reviewId}/participants/${employeeId}/reviewer-types/${reviewTypeId}/leave-feedback`,
      {}
    );
  },
};
